import React, {
	useRef,
	useCallback,
	useMemo,
	useEffect,
	useState,
	Fragment,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useFormik } from "formik";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import { NOTIFICATION_REFERENCE_PARAM } from "common/notification-routes";
import queryParamsEntries from "common/query-params-entries";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppEmptyState from "components/app-empty-state";
import AppSearchInput from "components/app-search-input";
import viewIcon from "assets/images/view-icon.svg";
import { formatDate, formatDateTime } from "common/calendar";
import { formatCurrency } from "common/format-currency-pattern";
import pdfIcon from "assets/images/pdf-icon.svg";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";
import exportIcon from "assets/images/pages/user-management/export-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import AppStatus from "components/app-status";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import AppCheckbox from "components/app-checkbox";
import AppClaimModal from "components/pages/claim-management/app-claim-modal";
import getGeneralClaimListing from "services/get-general-claim-listing";
import AppCalendarInput from "components/app-calendar-input";
import { CALENDAR_FORMAT, isDateBeforeToday, formatDateAndTime, isEndDateBeforeStartDate } from "common/calendar";
import AppSelectInput from "components/app-select-input";
import capitalizeCharacter from "common/capitalize-character";
import ERRORS from "common/errors";
import * as yup from "yup";
import placeholderFormat from "common/placeholder-format";
import AppConfirmDeleteModal from "components/pages/claim-management/app-confirm-delete-claim";


const PageCompanyClaimHistory = () => {
	const searchInputRef = useRef();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const claimModalRef = useRef();
	const confirmDeleteRef = useRef();
	const [anchorEl, setAnchorEl] = useState(null);
	const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;
	const [isExporting, setIsExporting] = useState(false);
	const [selectedClaim, setSelectedClaim] = useState(false);

	const paramsRef = useRef({
		page: parseInt(searchParams.get("page")) || 1,
		sort: searchParams.get("sort") || "",
		name: searchParams.get("name") || "",
		nickname: searchParams.get("nickname") || "",
		email: searchParams.get("email") || "",
		employeeId: searchParams.get("employeeId") || "",
		statusListStr: searchParams.get("statusListStr") || "",
		claimType: searchParams.get("employmentStatus") || "",
		createdStartDate: searchParams.get("createdStartDate") || "",
		createdEndDate: searchParams.get("createdEndDate") || "",
		startDate: searchParams.get("startDate") || "",
		endDate: searchParams.get("endDate") || ""
	});
	const notificationRefId = useRef(searchParams.get(NOTIFICATION_REFERENCE_PARAM) || "");

	const [data, setData] = useState({
		page: paramsRef.current.page,
		size: 10,
		total: 0,
		offset: 0,
		totalElements: 0,
		prev: false,
		next: false,
		items: [],
	});

	const onHandleRemove = () => {
		setAnchorEl(null);
		confirmDeleteRef.current.onHandleShow(selectedClaim.id);
	};

	const onHandleView = () => {
		setAnchorEl(null);
		claimModalRef.current.onHandleShow(selectedClaim.id);
	};

	const onHandleCloseMenu = () => {
		setAnchorEl(null);
	};

	const onHandleTableMenu = (event, { row }) => {
		setSelectedClaim(row.original);
		setAnchorEl(event.currentTarget);
	};

	const isEmptyState = useMemo(
		() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status && !paramsRef.current.employeeId && !paramsRef.current.claimType && !paramsRef.current.name && !paramsRef.current.nickname && !paramsRef.current.email && !paramsRef.current.employmentStatus && !paramsRef.current.createdStartDate && !paramsRef.current.createdEndDate && !paramsRef.current.statusListStr && !paramsRef.current.startDate && !paramsRef.current.endDate, [data.status]
	);

	const AttachmentCell = useCallback(({ row }) => {
		const file = row.original?.companyClaimApplicationFiles?.[0];
		return (<AppTableCell center value={file ? (<a href={file?.fileURL} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attacment" /></a>) : ("-")} />);
	}, []);

	//prettier-ignore
	const MenuCell = useCallback(( obj ) => {
		return <AppTableMoreIcon onClick={(event) => onHandleTableMenu(event, obj)} />;
	}, []);

	/* ----------------- render ----------------- */
	const columns = useMemo(
		() => [
			{
				Header: "Employee ID",
				title: "Employee ID",
				accessor: "employeeId",
				disableSortBy: false,
				Cell: ({ value }) => value || "-",
			},
			{
				Header: "Name",
				title: "Name",
				accessor: "name",
				disableSortBy: false,
				Cell: ({ row }) =>
					<div className="name-column">
						<AppTableCell left value={row.original.name || "-"} />
					</div>
				,
			},
			{
				Header: "Nickname",
				title: "Nickname",
				accessor: "nickname",
				disableSortBy: false,
				Cell: ({ row }) => <AppTableCell left value={row.original.nickname || "-"} />,
			},
			{
				Header: "Claim Type",
				title: "Claim Type",
				accessor: "claimTypeMapped",
				disableSortBy: false,
				Cell: ({ value }) => value || "-",
			},
			{
				Header: "Email",
				title: "Email",
				accessor: "email",
				disableSortBy: false,
				Cell: ({ value }) => value || "-",
			},

			{
				Header: "Amount (RM)",
				title: "Amount (RM)",
				id: "claimValue",
				accessor: "claimValue",
				disableSortBy: false,
				Cell: ({ row }) => (
					<AppTableCell center value={formatCurrency(row.original.claimValue)} />
				),
			},
			{
				Header: () => <div className="table__attachment">Attachment</div>,
				title: "Attachment",
				accessor: "attachment",
				disableSortBy: true,
				Cell: AttachmentCell,
			},
			{
				Header: () => <div className="table__e-invoice-exempted">E-Invoice Exempted</div>,
				title: "E-Invoice Exempted",
				accessor: "eInvoiceExempted",
				disableSortBy: true,
				Cell: ({ row }) => (
					<AppTableCell center value={row.original.eInvoiceExempted ? "Yes" : "No"} />
				),
			},
			{
				Header: "Submit Date",
				title: "Submit Date",
				accessor: "createdDate",
				disableSortBy: false,
				Cell: ({ row }) => (
					<AppTableCell left value={formatDate(row.original.createdDate)} />
				),
			},
			{
				Header: "Visit Date",
				title: "Visit Date",
				accessor: "claimDate",
				disableSortBy: false,
				Cell: ({ row }) =>
					<div className="claim-date-column">
						<AppTableCell left value={formatDate(row.original.actualDate)} />
					</div>
				,
			},
			{
				Header: "Status",
				title: "Status",
				accessor: "status",
				disableSortBy: false,
				Cell: ({ row }) => <AppStatus status={row.original.status || "-"} />,
			},
			{
				Header: "Reject Reason",
				title: "Reject Reason",
				id: "rejectReason",
				accessor: "rejectReason",
				disableSortBy: false,
				Cell: ({ value }) => value || "-",
			},
			{
				Header: "Remarks",
				title: "Remarks",
				id: "remarks",
				accessor: "remarks",
				disableSortBy: false,
				Cell: ({ value }) => value || "-",
			},
			{
				Header: "Last Update by",
				title: "Last Update by",
				accessor: "lastModifiedDate",
				disableSortBy: false,
				Cell: ({row}) => <AppTableCell left value={`${row.original.lastModifiedByName || "-"}\n${formatDateTime(new Date(row.original.lastModifiedDate))}`} />,
			},
			{
				Header: "",
				title: "",
				accessor: "*",
				disableSortBy: true,
				Cell: MenuCell,
			},
		],
		[AttachmentCell, MenuCell]
	);

	const [visibleColumns, setVisibleColumns] = useState(columns.map(c => c.accessor).filter(c => defaultNoDisplayHeaders(c)));

	const onHandleExport = useCallback(async () => {
		setIsExporting(true);

		const visibleColumnsFiltered = visibleColumns.filter((col) => col !== "*" && col !== "**");

		const visibleHeaders = columns.filter((col) => visibleColumnsFiltered.includes(col.accessor)).map((col) => col.title);

		const values = { ...paramsRef.current }

		let response = null;
		let fileName = null;

		try {
			const payload = {
				headers: visibleHeaders,
				columnFilters: visibleColumnsFiltered,
				searchFilters: {
					employeeId: values.employeeId,
					name: values.name,
					nickname: values.nickname,
					email: values.email,
					claimType: values.claimType,
					statuses: values.statusListStr,
					actualDateFrom: values.startDate,
					actualDateTo: values.endDate,
					createdDateFrom: values.createdStartDate,
					createdDateTo: values.createdEndDate,
				},
				sortFilter: values.sort,
			}

			const transformResponse = (data, headers) => {
				fileName = headers?.["content-disposition"]?.split("attachment; filename=")?.[1];

				if (fileName) return data;

				try {
					const jsonResponse = JSON.parse(new TextDecoder().decode(data));
					if (jsonResponse) return jsonResponse;
				} catch (error) {
					return data;
				}
			};

			response = await api.post.claims.export(payload, { transformResponse });
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			setIsExporting(false);
		}

		if (response) {
			const a = document.createElement("a");
			document.body.appendChild(a);
			const url = window.URL.createObjectURL(new Blob([response]), { type: "application/octet-stream" });
			a.href = url;
			a.download = fileName;
			a.click();
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);
			}, 1000);
		}
	}, [visibleColumns, columns]);

	const onHandleShowAdvanceSearch = (event) => {
		if (searchInputRef.current?.onHandleShowAdvanceSearch) {
			searchInputRef.current.onHandleShowAdvanceSearch(event);
		}
	};

	const headerButtons = useMemo(() => {
		const buttons = [
			{ label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
			{ label: "Export", icon: exportIcon, outline: true, onClick: onHandleExport, disabled: isExporting, showLoadingDots: isExporting },
		];
		return buttons;
	}, [onHandleExport, isExporting]);


	const initialValues = useMemo(() => ({ actualDate: "", claimType: "", email: "", employeeId: "", name: "", nickname: "", createdStartDate: "", createdEndDate: "", startDate: "", endDate: "", statusListStr: "" }), []);


	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			createdStartDate: yup.string().test("createdStartDate", ERRORS.START_DATE, function (value) {
				if (!value || !this.parent.createdEndDate)
					return true;

				return isDateBeforeToday(value, this.parent.createdEndDate);
			}),
			createdEndDate: yup.string().test("createdEndDate", ERRORS.END_DATE, function (value) {
				if (!value || !this.parent.createdStartDate)
					return true;

				return isDateBeforeToday(this.parent.createdStartDate || new Date(), value);
			}),
			startDate: yup.string().test("startDate", ERRORS.START_DATE, function (value) {
				if (!value || !this.parent.endDate)
					return true;

				return isDateBeforeToday(value, this.parent.endDate);
			}),
			endDate: yup.string().test("endDate", ERRORS.END_DATE, function (value) {
				if (!value || !this.parent.startDate)
					return true;

				return isDateBeforeToday(this.parent.startDate || new Date(), value);
			}),
		}),
		onSubmit: (values) => {
			onHandleSubmitSearch(values);
		},
	});

	//prettier-ignore
	const onHandleStartDate = useCallback((name, value) => {
		formik.setFieldValue(name, value);

		if (isEndDateBeforeStartDate(value, formik.values.endDate)) formik.setFieldValue("endDate", "");
	}, [formik]);

	//prettier-ignore
	const onHandleCreatedStartDate = useCallback((name, value) => {
		formik.setFieldValue(name, value);

		if (isEndDateBeforeStartDate(value, formik.values.createdEndDate)) formik.setFieldValue("createdEndDate", "");
	}, [formik]);

	const setValues = useMemo(() => formik.setValues, [formik]);

	const advanceSearchValues = useMemo(() => {
		const params = queryParamsEntries(searchParams);
		return {
			name: params.name, nickname: params.nickname, employeeId: params.employeeId, claimType: params.claimType, email: params.email, statusListStr: capitalizeCharacter(params.statusListStr?.split("_")?.join(" ")),
			createdStartDate: params.createdStartDate,
			createdEndDate: params.createdEndDate,
			startDate: params.startDate,
			endDate: params.endDate
		};
	}, [searchParams]);

	/* ----------------- helpers ----------------- */
	const onHandleRemoveField = (field) => {
		formik.setFieldValue(field, "");
		paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };
		onHandleGetList();
	};

	const onHandleResetSearch = () => {
		setVisibleColumns(columns.map(c => c.accessor).filter(c => defaultNoDisplayHeaders(c)));
		formik.setValues(formik.initialValues);
		paramsRef.current = { page: 1, sort: "", ...formik.initialValues };
		onHandleGetList();
	};

	const onHandleSubmitSearch = (values) => {
		searchInputRef.current.onhandleCloseAdvanceSearch();
		paramsRef.current = { ...paramsRef.current, page: 1, ...values };
		onHandleGetList();
	};

	/* ----------------- load data ----------------- */
	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData({
			page: paramsRef.current.page,
			size: 10,
			total: 0,
			offset: 0,
			totalElements: 0,
			prev: false,
			next: false,
			items: [],
		});

		try {
			const payload = { ...paramsRef.current, size: 10 };
			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });
			payload.page = paramsRef.current.page - 1;
			if (payload.createdStartDate) {
				payload.createdStartDate = formatDateAndTime(new Date(payload.createdStartDate));
			}

			if (payload.createdEndDate) {
				payload.createdEndDate = formatDateAndTime(new Date(payload.createdEndDate));
			}

			if (payload.startDate) {
				payload.startDate = formatDateAndTime(new Date(payload.startDate));
			}

			if (payload.endDate) {
				payload.endDate = formatDateAndTime(new Date(payload.endDate));
			}

			response = await api.get.claims.list(sanitizeObject(payload));

		} catch (error) {
			serveRequestErrors(error);
		}

		setData((prev) => ({
			...prev,
			page: paramsRef.current.page,
			prev: response ? !response.first : false,
			next: response ? !response.last : false,
			items: response?.content ?? [],
			total: response ? response.totalPages : 1,
			totalElements: response ? response.totalElements : response?.content?.length || 0,
			offset: response?.pageable?.offset || 0,
			status: response?.pageable,
		}));
	}, []);

	/* ----------------- table callbacks ----------------- */
	const onHandlePagination = useCallback(
		(event) => {
			const ctrl = event.currentTarget?.getAttribute("data-ctrl");
			if (ctrl === "prev" && paramsRef.current.page > 1) {
				paramsRef.current.page -= 1;
			} else if (ctrl === "next" && paramsRef.current.page < data.total) {
				paramsRef.current.page += 1;
			} else if (!ctrl) {
				paramsRef.current.page = event.target.value;
			}
			onHandleGetList();
		},
		[onHandleGetList, data.total]
	);

	const onHandleSort = useCallback((id, order) => {
		const sortMap = {
			employeeId: "employeeId",
			name: "name",
			nickname: "nickname",
			claimTypeMapped: "claimType",
			email: "email",
			claimValue: "claimValue",
			createdDate: "createdDate",
			claimDate: "actualDate",
			status: "status",
		};
		
		const backendField = sortMap[id] ?? id;
		paramsRef.current.sort = backendField + (order ? `,${order}` : "");
		paramsRef.current.page = 1;
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	// opens the detail modal when arriving from a notification, then strips the
	// param so the same notification can be clicked again.
	useEffect(() => {
		const referenceId = searchParams.get(NOTIFICATION_REFERENCE_PARAM) || notificationRefId.current;

		if (!referenceId) return;

		notificationRefId.current = "";

		claimModalRef.current?.onHandleShow(referenceId);

		memoSetSearchParams.current(sanitizeObject({ ...paramsRef.current, size: 10 }), { replace: true });
	}, [searchParams]);

	useEffect(() => {
		const { page, sort, ...res } = paramsRef.current;
		setValues((prev) => ({ ...prev, ...res }));
	}, [setValues]);

	const toggleColumnVisibility = (accessor) => {
		setVisibleColumns((prev) => {
			if (!prev.includes(accessor)) return [...prev, accessor];

			const next = prev.filter((val) => val !== accessor);
			const hasDataColumn = next.some((val) => val && val !== "*" && val !== "**");

			return hasDataColumn ? next : prev;
		});
	};


	function defaultNoDisplayHeaders(col) {
		if (col === "employeeId") return false;
		if (col === "email") return false;
        if (col === "nickname") return false;
        if (col === "rejectReason") return false;
        if (col === "remarks") return false;
        return true;
    }

	return (
		<div className="page-company-claim-history">
			<div className="company-claim-history">
				<div className="app-claim-history">
					{isEmptyState && (
						<AppEmptyState
							title="No records found"
							description="You don’t have any records yet"
							disabledButton
						/>
					)}
					{!isEmptyState && (
						<Fragment>
							<AppSearchInput
								ref={searchInputRef}
								multiValues={advanceSearchValues}
								onRemoveField={onHandleRemoveField}

								buttons={headerButtons}
							>
								<form
									className="app-advance-search-form"
									onSubmit={formik.handleSubmit}
								>
									<div className="advance-form">
										<div className="advance-form__inputs">
											<span className="advance-form__inputs__toggle-word">Select Columns to Display:</span>
											<div className="advance-form__inputs__checkbox">
												{columns
													.filter((col) => col.accessor && col.accessor !== "*" && col.accessor !== "**")
													.map((col) => (
														<div className="checkbox-wrapper" key={col.accessor}>
															<AppCheckbox
																key={col.accessor}
																name={col.accessor}
																value={visibleColumns.includes(col.accessor)}
																onClick={() => toggleColumnVisibility(col.accessor)}
																label={col.title}
															/>
														</div>
													))}
											</div>

											<AppInput
												type="text"
												name="employeeId"
												label="Employee ID"
												placeholder="Enter employee ID"
												value={formik.values.employeeId}
												onChange={formik.handleChange}
											/>

											<AppInput
												type="text"
												name="name"
												label="Name"
												placeholder="Enter employee name"
												value={formik.values.name}
												onChange={formik.handleChange}
											/>

											<AppInput
												type="text"
												name="nickname"
												label="Employee Nickname"
												placeholder="Enter employee Nickname"
												value={formik.values.nickname}
												onChange={formik.handleChange}
											/>

											<AppInput
												type="text"
												name="email"
												label="Employee Email"
												placeholder="Enter employee email"
												value={formik.values.email}
												onChange={formik.handleChange}
											/>

											<AppSelectInput name="claimType" label="Employment Claim Types" placeholder="Select Employment Status" loadOptions={getGeneralClaimListing} value={formik.values.claimType} onChange={formik.handleChange} />

											<AppSelectInput name="statusListStr" label="Status" placeholder="Select Claims Status" options={COMMON.DROPDOWNS.INSURANCE_SEARCH_CLAIM_STATUS} value={formik.values.statusListStr} onChange={formik.handleChange} />
										</div>

										<div className="advance-form__inputs advance-form__claim_history_search">
											<AppCalendarInput name="startDate" label="Visit Date From" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.startDate} error={formik.errors.startDate} touched={formik.touched.startDate} onChange={onHandleStartDate} />

											<AppCalendarInput name="endDate" label="Visit Date To" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.startDate} value={formik.values.endDate} error={formik.errors.endDate} touched={formik.touched.endDate} onChange={formik.setFieldValue} />

											<AppCalendarInput name="createdStartDate" label="Submission Date From" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.createdStartDate} error={formik.errors.createdStartDate} touched={formik.touched.createdStartDate} onChange={onHandleCreatedStartDate} />

											<AppCalendarInput name="createdEndDate" label="Submission Date To" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.createdStartDate} value={formik.values.createdEndDate} error={formik.errors.createdEndDate} touched={formik.touched.createdEndDate} onChange={formik.setFieldValue} />
										</div>

										<div className="advance-form__button-container">
											<AppButton
												type="button"
												label="Clear"
												outline
												onClick={onHandleResetSearch}
											/>
											<AppButton
												type="submit"
												label="Search"
											/>
										</div>

									</div>
								</form>
							</AppSearchInput>

							{(paramsRef.current.employeeId || paramsRef.current.email || paramsRef.current.claimType || paramsRef.current.name 
								|| paramsRef.current.nickname || paramsRef.current.startDate || paramsRef.current.statusListStr 
								|| paramsRef.current.createdStartDate || paramsRef.current.createdEndDate || paramsRef.current.endDate 
								|| paramsRef.current.startDate) && (
									<div className="claim-history__results">
										<p className="claim-history__text">{data.totalElements} results found</p>
									</div>
								)}

							<AppTable
								pages={data}
								onHandlePagination={onHandlePagination}
								onHandleSort={onHandleSort}
								columns={columns.filter(col => visibleColumns.includes(col.accessor))}
							/>

							{/* prettier-ignore */}
							<Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onHandleCloseMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
								<MenuItem onClick={onHandleView}><img className="app-table-menu__icon" src={viewIcon} alt="view" />View</MenuItem>
								{selectedClaim?.status === COMMON.STATUS_ID.APPROVED && (<MenuItem onClick={onHandleRemove}><img className="app-table-menu__icon" src={removeIcon} alt="view" />Cancel</MenuItem>)}
							</Menu>

						</Fragment>
					)}

					<AppClaimModal ref={claimModalRef} />
					<AppConfirmDeleteModal ref={confirmDeleteRef} onHandleGetList={onHandleGetList}/>
				</div>
			</div>
		</div>
	);
};

export default PageCompanyClaimHistory;
